import { TrackingEventIds } from "./constants/TrackingEventIds";
import { TrackingVerifier } from "./verification/TrackingVerifier";
import { TrackingLoader } from "./TrackingLoader";
import { ImpressionTrackingEvent } from "./ImpressionTrackingEvent";
import { ConnectionTrackingEvent } from "./ConnectionTrackingEvent";
import { ClientLoadTrackingEvent } from "./ClientLoadTrackingEvent";
import { ABTestTimeoutTrackingEvent } from "./ABTestTimeoutTrackingEvent";
import { WorldAssignmentTrackingEvent } from "./WorldAssignmentTrackingEvent";
import { ProfilingTrackingEvent } from "./ProfilingTrackingEvent";
import { GamePaymentShopClickEvent } from "./GamePaymentShopClickEvent";
import { DesktopDeviceInformationTrackingEvent } from "./DesktopDeviceInformationTrackingEvent";
import { ShopCheckoutTrackingEvent } from "./ShopCheckoutTrackingEvent";
import { InGamePerformanceEvent } from "./InGamePerformanceEvent";
import { InvitationTrackingEvent } from "./InvitationTrackingEvent";
import { FacebookConnectionTrackingEvent } from "./FacebookConnectionTrackingEvent";
import { FacebookUserDataTrackingEvent } from "./FacebookUserDataTrackingEvent";
import { ShopLoadedTrackingEvent } from "./ShopLoadedTrackingEvent";
import { ShopBackButtonTrackingEvent } from "./ShopBackButtonTrackingEvent";
import { AssetLoadingSpeedTrackingEvent } from "./AssetLoadingSpeedTrackingEvent";
import { BasicTrackingEvent } from "./BasicTrackingEvent";
import { TrackingConstants } from "./TrackingConstants";
import { URLRequest } from "@goodgamestudios/createjs-utilities";
import { getLogger } from "@goodgamestudios/logger";
import { BasicModel } from "../basic/model/BasicModel";
import { NetworkInstance } from "@goodgamestudios/world-assignment";
import * as trackingEvents from "@goodgamestudios/tracking-event-types";
import { isNullOrUndefined } from "util";
import { DeviceId } from "./constants/DeviceId";
import { ConnectionType } from "./constants/ConnectionType";
import { StoreId } from "./constants/StoreId";

const logger = getLogger("Tracking.TrackingCache");

export class TrackingCache {
    public static VERSION: string = "$Id$";
    private static trackingCache: TrackingCache;

    private _gameId: number = 0;
    private _referrer: string = "";
    private _accountId: string = "";
    private _networkId: number = 0;
    private _distributorId: number = 0;
    private _language: string = "";

    private _isInitialized = false;

    constructor(private _trackingVerifier = new TrackingVerifier(), private _currentCache = new Map<any, any>()) {
    }

    public static getInstance(): TrackingCache {
        if (!TrackingCache.trackingCache) {
            TrackingCache.trackingCache = new TrackingCache();
        }
        return TrackingCache.trackingCache;
    }


    public init(gameId: number, networkId: number, referrer: string, accountId: string, language: string,): void {
        this._gameId = gameId;
        this._networkId = networkId;
        this._referrer = referrer;
        this._language = language;
        this._accountId = accountId;

        //  todo  html5  do we still really need such a cache?  benefits in terms of pooling are minimal, and risk of polluted data high..
        this._currentCache.set(TrackingEventIds.IMPRESSION, new ImpressionTrackingEvent(referrer));
        this._currentCache.set(TrackingEventIds.CONNECTION, new ConnectionTrackingEvent(referrer));
        this._currentCache.set(TrackingEventIds.CLIENT_LOAD, new ClientLoadTrackingEvent(referrer));
        this._currentCache.set(TrackingEventIds.AB_TEST_TIMEOUT_TRACKING, new ABTestTimeoutTrackingEvent(referrer));
        this._currentCache.set(TrackingEventIds.WORLD_ASSIGNMENT, new WorldAssignmentTrackingEvent(referrer));
        this._currentCache.set(TrackingEventIds.PROFILING, new ProfilingTrackingEvent(referrer));
        this._currentCache.set(TrackingEventIds.GAME_PAYMENT_SHOP_CLICK, new GamePaymentShopClickEvent(referrer));
        this._currentCache.set(TrackingEventIds.DESKTOP_DEVICE_INFORMATION, new DesktopDeviceInformationTrackingEvent(referrer));
        this._currentCache.set(TrackingEventIds.SHOP_CHECKOUT, new ShopCheckoutTrackingEvent(referrer));
        this._currentCache.set(TrackingEventIds.IN_GAME_PERFORMANCE, new InGamePerformanceEvent(referrer));
        this._currentCache.set(TrackingEventIds.INVITATION, new InvitationTrackingEvent(referrer));
        this._currentCache.set(TrackingEventIds.FACEBOOK_CONNECTION, new FacebookConnectionTrackingEvent(referrer));
        this._currentCache.set(TrackingEventIds.FACEBOOK_USER_DATA, new FacebookUserDataTrackingEvent(referrer));
        this._currentCache.set(TrackingEventIds.SHOP_LOADED, new ShopLoadedTrackingEvent(referrer));
        this._currentCache.set(TrackingEventIds.SHOP_BACK_BUTTON, new ShopBackButtonTrackingEvent(referrer));
        this._currentCache.set(TrackingEventIds.ASSETS_LOADING_SPEED, new AssetLoadingSpeedTrackingEvent(referrer));

        this._isInitialized = true;
    }

    public cacheEvent(eventId: number, event: BasicTrackingEvent) {
        this._currentCache.set(eventId, event);
    }

    public getEvent(eventId: number): BasicTrackingEvent {
        return this._currentCache.get(eventId);
    }

    public getAllEvents(): any[] {
        return Array.from(this._currentCache.keys());
    }

    public sendEvent(eventId: number): boolean {
        // sanity check
        if (!this.isInitialized) {
            logger.warn("tracking cache is not initalized");
            return false;
        }

        const evt = this.prepareEvent(eventId);
        const json = JSON.stringify(evt);

        if (this.validateTests) {
            const validationResult = trackingEvents.validateTrackingEvent(evt);
            const endpoint = validationResult.success ? this.testServer + "events" : this.testServer + `events/invalid?reason=${encodeURIComponent(validationResult["message"])}`;
            new TrackingLoader(new URLRequest(endpoint, json, "POST"));
        }
        new TrackingLoader(new URLRequest(TrackingConstants.JSON_TRACKING_URL, json, "POST"));
        // Tracking verification
        this._trackingVerifier.keepEvent(eventId);
        return true;
    }

    private get currentInstance(): NetworkInstance {
        return BasicModel.instanceProxy.selectedInstanceVO;
    }

    private prepareEvent(eventId: number): {eventId: number} {
        let event: BasicTrackingEvent = (<BasicTrackingEvent>this._currentCache.get(eventId));
        let vars: any = event.getVars();
        if (!vars.eventId) {
            vars.eventId = eventId;
            //TODO HTML5 check if we really need this.  eventID should come from the class itself
            logger.warn("assigning ID to tracking event ", eventId);
        } else if (eventId !== vars.eventId) {
            logger.warn(`Expected event with with eventId ${eventId}, but found ${vars.eventId}`);
        }
        vars.gameId = this._gameId;
        vars.networkId = this._networkId;
        vars.accountId = this._accountId;
        // TODO HTML5   ADD ALSO PLAYERID HERE ??
        if (vars.hasOwnProperty("playerId") && isNullOrUndefined(vars["playerId"]) || vars["playerId"] == 0) {
            logger.warn("we are sending an event without playerID", vars)
        }


        // TODO HTML5  would be better to avoid this checks and dependencies
        // TODO HTML5  check if behaviour shared to all events or only for ClientFunnel
        if (!this.currentInstance) {
            logger.warn(`sending tracking ${eventId} but currentInstance is null will try to use networkCookie`);
            vars.zoneId = BasicModel.networkCookie.zoneId || 0;
            vars.instanceId = BasicModel.networkCookie.instanceId || -1;
        } else {
            vars.instanceId = this.currentInstance.instanceId;
            vars.zoneId = this.currentInstance.zoneId;
        }
        vars.lang = this._language;

        this.fixMissingValues(vars);

        vars.html5 = 1;

        return vars;
    }

    /**
     *  TODO HTML5  LOTS OF EVENT STILL REQUIRE SOME VALUES THAT ARE NOT YET CLEAR WHERE THEY COME FROM OR HOW TO SET THEM. **** temporarily *** fill it with default values!
     * @param vars
     */
    public  fixMissingValues(vars: any) {
        const fix = (prop, val) => {
            if (vars.hasOwnProperty(prop) && isNullOrUndefined(vars[prop]) || vars[prop] == 0) {
                logger.warn(`event ${vars.eventId}: autofilling property ${prop} with default value ${val}`);
                vars[prop] = val
            }
        }
        fix("storeId", StoreId.WEB);
        fix("deviceId", DeviceId.DESKTOP);
        fix("connectionType", ConnectionType.LAN);
    }

    public get isInitialized(): boolean {
        return this._isInitialized;
    }

    public get trackingVerifier(): TrackingVerifier {
        return this._trackingVerifier;
    }

    public set networkId(value: number) {
        this._networkId = value;
    }

    private validateTests: boolean = false;
    private testServer: string;

    public useTestValidationServer(value: boolean, url: string = TrackingConstants.TEST_TRACKING_SERVER) {
        this.validateTests = value;
        this.testServer = url;
    }
}
